import {
  ArrowDown,
  Clock,
  FlaskConical,
  Mail,
  MessageSquare,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react';
/**
 * Journey builder — node-based visualization of a multi-step drip campaign.
 *
 * Each step is a card showing channel, delay, subject, A/B variants. Live API:
 *   GET    /api/marketing/campaigns/:id/steps
 *   POST   /api/marketing/campaigns/:id/steps
 *   PATCH  /api/marketing/campaigns/:id/steps/:stepId
 *   DELETE /api/marketing/campaigns/:id/steps/:stepId
 *   POST   /api/marketing/campaigns/:id/steps/:stepId/variants
 *   DELETE /api/marketing/campaigns/:id/steps/:stepId/variants/:variantId
 *
 * Best-in-class kennel SaaS feature.
 */
import { useCallback, useEffect, useState } from 'react';

type StepChannel = 'email' | 'sms' | 'whatsapp' | 'push';

interface Variant {
  id: string;
  variantLabel: string;
  subject: string;
  bodyOverride: string | null;
  allocationPercent: number;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  isWinner: number;
}

interface Step {
  id: string;
  campaignId: string;
  stepOrder: number;
  delayDays: number;
  channel: StepChannel;
  subject: string | null;
  bodyOverride: string | null;
  abortIfConditionJson: string | null;
  sendTimeOptimised: number;
}

interface StepWithVariants {
  step: Step;
  variants: Variant[];
}

function ChannelIcon({ channel }: { channel: StepChannel }) {
  if (channel === 'email') return <Mail className="size-4" />;
  if (channel === 'push') return <Zap className="size-4" />;
  return <MessageSquare className="size-4" />;
}

export function JourneyBuilder({ campaignId }: { campaignId: string }) {
  const [steps, setSteps] = useState<StepWithVariants[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/marketing/campaigns/${campaignId}/steps`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setError(`Failed to load journey (${res.status})`);
        return;
      }
      const body = (await res.json()) as { steps?: StepWithVariants[] };
      setSteps(body.steps ?? []);
      setError(null);
    } catch {
      setError('Network error');
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addStep() {
    setSaving(true);
    const nextOrder = steps?.length ?? 0;
    try {
      const res = await fetch(`/api/marketing/campaigns/${campaignId}/steps`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          stepOrder: nextOrder,
          delayDays: nextOrder === 0 ? 0 : 3,
          channel: 'email',
          subject: 'Step ' + (nextOrder + 1),
        }),
      });
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { issues?: string[] };
        setError(b.issues?.join(', ') ?? `Add failed (${res.status})`);
        return;
      }
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function patchStep(stepId: string, patch: Partial<Step>) {
    await fetch(`/api/marketing/campaigns/${campaignId}/steps/${stepId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    });
    await load();
  }

  async function deleteStep(stepId: string) {
    if (!confirm('Delete this step? Customers in this step will skip to the next.')) return;
    await fetch(`/api/marketing/campaigns/${campaignId}/steps/${stepId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    await load();
  }

  async function addVariant(stepId: string) {
    const label = prompt('Variant label (A/B/C):');
    if (!label) return;
    const subject = prompt('Subject line for this variant:');
    if (!subject) return;
    const allocationStr = prompt('Allocation percent (1-100):', '50');
    const allocationPercent = allocationStr ? Math.floor(Number(allocationStr)) : 50;
    await fetch(`/api/marketing/campaigns/${campaignId}/steps/${stepId}/variants`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ variantLabel: label, subject, allocationPercent }),
    });
    await load();
  }

  async function deleteVariant(stepId: string, variantId: string) {
    await fetch(`/api/marketing/campaigns/${campaignId}/steps/${stepId}/variants/${variantId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    await load();
  }

  if (steps === null) return <div className="text-sm text-muted-foreground">Loading journey…</div>;

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {steps.length === 0 && (
          <div className="rounded border border-dashed border-border p-6 text-sm text-muted-foreground">
            No steps yet — this campaign sends as a single one-shot blast. Add steps below to turn
            it into a multi-step journey.
          </div>
        )}

        {steps.map(({ step, variants }, idx) => (
          <div key={step.id}>
            {idx > 0 && (
              <div className="flex items-center justify-center text-xs text-muted-foreground py-1">
                <ArrowDown className="size-4 mr-1" /> Wait {step.delayDays} day
                {step.delayDays === 1 ? '' : 's'}
              </div>
            )}
            <div className="rounded-lg border border-border bg-background p-4 space-y-3 shadow-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="rounded-full bg-secondary px-2 py-1 text-xs num">
                    Step {step.stepOrder + 1}
                  </div>
                  <ChannelIcon channel={step.channel} />
                  <select
                    value={step.channel}
                    onChange={(e) =>
                      void patchStep(step.id, { channel: e.target.value as StepChannel })
                    }
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value="email">email</option>
                    <option value="sms">sms</option>
                    <option value="whatsapp">whatsapp</option>
                    <option value="push">push</option>
                  </select>
                  <label className="flex items-center gap-1 text-xs">
                    <Clock className="size-3" />
                    Delay
                    <input
                      type="number"
                      min={0}
                      value={step.delayDays}
                      onChange={(e) =>
                        void patchStep(step.id, {
                          delayDays: Math.max(0, Math.floor(Number(e.target.value))),
                        })
                      }
                      className="w-16 border rounded px-1 py-0.5 text-xs num"
                    />
                    days
                  </label>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={step.sendTimeOptimised === 1}
                      onChange={(e) =>
                        void patchStep(step.id, { sendTimeOptimised: e.target.checked ? 1 : 0 })
                      }
                    />
                    Best send time
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => void deleteStep(step.id)}
                  className="text-red-600 hover:bg-red-50 rounded p-1"
                  aria-label="Delete step"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>

              <div className="space-y-1">
                <input
                  type="text"
                  value={step.subject ?? ''}
                  placeholder="Subject"
                  onChange={(e) => void patchStep(step.id, { subject: e.target.value })}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
                <textarea
                  rows={3}
                  value={step.bodyOverride ?? ''}
                  placeholder="Body override (leave blank to use campaign body)"
                  onChange={(e) =>
                    void patchStep(step.id, { bodyOverride: e.target.value || null })
                  }
                  className="w-full border rounded px-2 py-1 text-xs font-mono"
                />
              </div>

              {/* Variants */}
              <div className="border-t border-border pt-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-medium flex items-center gap-1">
                    <FlaskConical className="size-3" /> A/B variants ({variants.length})
                  </div>
                  <button
                    type="button"
                    onClick={() => void addVariant(step.id)}
                    className="text-xs rounded border px-2 py-1 hover:bg-secondary"
                  >
                    + Variant
                  </button>
                </div>
                {variants.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    No variants — step uses the subject above for every recipient.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {variants.map((v) => {
                      const openRate =
                        v.sentCount > 0 ? Math.round((v.openedCount / v.sentCount) * 100) : 0;
                      return (
                        <li
                          key={v.id}
                          className={`flex items-center justify-between text-xs rounded border px-2 py-1 ${
                            v.isWinner === 1 ? 'border-emerald-500 bg-emerald-50' : 'border-border'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium num">{v.variantLabel}</span>
                            <span className="text-muted-foreground truncate max-w-[20rem]">
                              {v.subject}
                            </span>
                            <span className="num">{v.allocationPercent}%</span>
                          </div>
                          <div className="flex items-center gap-2 num">
                            <span title="sent">{v.sentCount} sent</span>
                            <span title="open rate">{openRate}% open</span>
                            <button
                              type="button"
                              onClick={() => void deleteVariant(step.id, v.id)}
                              className="text-red-600 hover:bg-red-50 rounded p-0.5"
                              aria-label="Delete variant"
                            >
                              <Trash2 className="size-3" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addStep}
        disabled={saving}
        className="flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm font-medium disabled:opacity-50"
      >
        <Plus className="size-4" /> Add step
      </button>
    </div>
  );
}
